/**
 * useSuppressibleCount — shared suppression logic for the matching-records
 * save-gates in IdentityVerificationSection + GuardianSection.
 *
 * Lifecycle:
 *   - `matches` is the current array from `useMatchingPatients`. We hold
 *     a local `suppressed` flag that the parent can toggle via
 *     `suppress()` (e.g. from the dialog's "Continue with new record"
 *     footer).
 *   - When the consumer toggles `suppressed = true`, `effectiveCount`
 *     becomes 0 and the save-gate clears — but the underlying React
 *     Query is untouched, so `matches` still holds the rows for the
 *     dialog table.
 *   - When the `matches` array **reference** changes (next fetch returns
 *     a new array), `suppressed` resets to `false` automatically — a
 *     new prompt is the right UX when the data signature changes (user
 *     edited the ID number, server returned a different match set).
 *
 * Tab-switch behavior: shadcn Tabs keeps inactive panels MOUNTED (only
 * the `data-active` panel is visible), so the section components stay
 * alive when the user switches tabs. Suppression persists across the
 * form session and only resets on the matches-reference change above.
 * That matches Flutter `UseRecordAnywayEvent` which clears the
 * matching-records BLoC state until the next FetchMatchingRecordsEvent
 * fires — same effective behaviour.
 *
 * Tested in `use-suppressible-count.test.ts`.
 *
 * **Caller contract:** pass a reference-stable `matches` value. In
 * production the only consumer is React Query's `useMatchingPatients`,
 * which returns the same array reference between fetches (only swaps
 * on real data change) — so `matches` is stable across unrelated
 * re-renders by construction. Don't call this hook with an inline
 * array literal (`useSuppressibleCount([])`) — every render would
 * produce a new reference and trip the reset effect.
 */

import { useEffect, useState } from 'react';

export interface SuppressibleCountResult {
  /** Count after suppression. 0 when the parent has dismissed the prompt
   *  and the matches haven't changed since. */
  effectiveCount: number;
  /** Raw count of matches before suppression — for popup labels. */
  rawCount: number;
  /** True when the parent has dismissed the prompt for this match set. */
  suppressed: boolean;
  /** Sets `suppressed = true`. Called from the dialog's "Continue with
   *  new record" footer. */
  suppress: () => void;
}

export function useSuppressibleCount<T>(matches: T[]): SuppressibleCountResult {
  const [suppressed, setSuppressed] = useState(false);

  // Reset on new matches reference. `useMatchingPatients` returns a new
  // array on every refetch (React Query semantics), so this fires exactly
  // when the server data signature changes — never on unrelated re-renders.
  useEffect(() => {
    setSuppressed(false);
  }, [matches]);

  const rawCount = matches.length;
  return {
    effectiveCount: suppressed ? 0 : rawCount,
    rawCount,
    suppressed,
    suppress: () => setSuppressed(true),
  };
}
